import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { Card, Field, Input, Textarea, Select, PageHeader, PrimaryButton, DangerButton } from "@/components/admin/AdminUI";
import { SeoGenerateButton } from "@/components/admin/SeoGenerateButton";
import { CERTIFICATION_CLAIM_TYPES, claimTypeDescription } from "@/lib/certificationTypes";

const EMPTY = {
  name: "", main_claim_type: "", organization: "", short_description: "",
  full_description: "", external_link: "",
  seo_title: "", seo_description: "", is_public: true,
};

export default function AdminCertificationEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const { data: cert } = useQuery({
    queryKey: ["admin-certification", id],
    queryFn: async () => (await api.get(`/admin/materials-certifications/${id}`)).data,
    enabled: !!id,
  });

  useEffect(() => { if (cert) setForm({ ...EMPTY, ...cert }); }, [cert]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : (e.target ? e.target.value : e) }));

  async function save() {
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/admin/materials-certifications", form);
        toast.success("Certification created");
        qc.invalidateQueries({ queryKey: ["admin-certifications"] });
        navigate(`/admin/materials-certifications/${res.data.id}`);
      } else {
        await api.patch(`/admin/materials-certifications/${id}`, form);
        toast.success("Saved");
        qc.invalidateQueries({ queryKey: ["admin-certifications"] });
        qc.invalidateQueries({ queryKey: ["admin-certification", id] });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  async function remove() {
    if (!window.confirm("Delete this certification? It will also be detached from any brands and clothing items currently using it.")) return;
    await api.delete(`/admin/materials-certifications/${id}`);
    toast.success("Deleted");
    qc.invalidateQueries({ queryKey: ["admin-certifications"] });
    navigate("/admin/materials-certifications");
  }

  return (
    <div data-testid="admin-certification-edit">
      <PageHeader
        title={isNew ? "New certification" : (form.name || "Certification")}
        subtitle={isNew ? "" : `/materials-certifications/${form.slug || ""}`}
        actions={<>
          <PrimaryButton onClick={save} disabled={saving} data-testid="cert-save-button">{saving ? "Saving…" : "Save"}</PrimaryButton>
          {!isNew && <DangerButton onClick={remove}>Delete</DangerButton>}
        </>}
      />

      <Card className="space-y-4">
        <Field label="Name"><Input value={form.name} onChange={set("name")} data-testid="cert-name-input" /></Field>
        <Field
          label="Main claim type"
          hint={claimTypeDescription(form.main_claim_type) || "Pick the strongest claim this certification verifies."}
        >
          <Select value={form.main_claim_type || ""} onChange={set("main_claim_type")} data-testid="cert-claim-type-select">
            <option value="">— Select claim type —</option>
            {CERTIFICATION_CLAIM_TYPES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </Select>
        </Field>
        <Field label="Organization / Standard owner (e.g. 'Global Standard gGmbH')">
          <Input value={form.organization || ""} onChange={set("organization")} maxLength={200} data-testid="cert-organization-input" />
        </Field>
        <Field label="Short description (one line shown on the certifications list card)">
          <Input value={form.short_description} onChange={set("short_description")} maxLength={200} data-testid="cert-short-desc-input" />
        </Field>
        <Field label="Full description (shown in modal and on the full page)">
          <Textarea rows={8} value={form.full_description} onChange={set("full_description")} data-testid="cert-full-desc-input" />
        </Field>
        <Field label="External link (official certification body URL)">
          <Input value={form.external_link || ""} onChange={set("external_link")} placeholder="https://global-standard.org" data-testid="cert-link-input" />
        </Field>
        <div className="flex items-center justify-between"><span className="text-sm">Public</span><input type="checkbox" checked={!!form.is_public} onChange={set("is_public")} /></div>

        <div className="border-t border-[#E9ECEF] pt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-wider text-[#6C757D]">SEO</h3>
            <SeoGenerateButton
              buildPayload={() => ({ kind: "certification", name: form.name, short_description: form.short_description, description: form.full_description })}
              onResult={({ seo_title, seo_description }) => setForm((f) => ({ ...f, seo_title, seo_description }))}
            />
          </div>
          <Field label="SEO title"><Input value={form.seo_title || ""} onChange={set("seo_title")} maxLength={70} /></Field>
          <Field label="SEO description"><Textarea rows={2} value={form.seo_description || ""} onChange={set("seo_description")} maxLength={170} /></Field>
        </div>
      </Card>
    </div>
  );
}
